import { integrationsRootKey } from '@/lib/integrations/settings-contracts';
import { documentEmojiKeys } from '@/hooks/document-emoji-keys';
import { EMAIL_INDEX_KEY } from '@/hooks/use-email-index/email-index-key';
import {
  CRM_IMPORT_JOBS_QUERY_KEY,
  FORWARDED_EMAIL_LIST_QUERY_KEY,
  EXTERNAL_EMAIL_MAILBOXES_QUERY_KEY,
  EXTERNAL_EMAIL_SEND_QUERY_KEY,
  MESSAGING_ACCOUNTS_QUERY_KEY,
  MESSENGER_WINDOW_QUERY_KEY,
  ON_BEHALF_SEND_CAPABILITY_QUERY_KEY,
  PASSKEYS_QUERY_KEY,
  POTENTIAL_DUPLICATES_QUERY_KEY,
  RECIPIENT_CONTACTS_QUERY_KEY,
  REJECTED_EMAILS_QUERY_KEY,
  SCHEDULED_DRAFTS_QUERY_KEY,
  UNSUBSCRIBE_INFO_QUERY_SUBKEY,
  VIEWER_SCOPED_EVIDENCE_ROOTS,
  WEBSITES_QUERY_KEY,
} from '@/hooks/pii-query-key-roots';
import {
  representationTaskOwnerKeys,
  representationTaskStageKeys,
} from '@/hooks/representation-task-owner-keys';

import { isShareGatedQuery } from './share-gated-cache-registry';

import type { Query } from '@tanstack/react-query';

const PERSISTED_QUERY_CACHE_KEY = 'REACT_QUERY_OFFLINE_CACHE';

interface QueryPersistenceStorage {
  readonly length?: number;
  getItem(key: string): string | null;
  key?(index: number): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * Keys holding constituent-derived state in plaintext: the query cache itself,
 * plus the V2 tab/scroll state whose labels are correspondence titles — an
 * ingested email's title IS its subject (`ingest-forwarded-email` writes
 * `title: 'Email: <subject>'`), so the tab strip is PII at rest just as the
 * cache is.
 */
const PLAINTEXT_SENSITIVE_STORAGE_PREFIXES = [
  PERSISTED_QUERY_CACHE_KEY,
  'v2-tabs',
  'v2-active-tab',
  'v2-recently-closed',
  'v2-scroll-positions',
  'pp:tasks:view-state:v1',
] as const;

function isPlaintextSensitiveStorageKey(key: string): boolean {
  return PLAINTEXT_SENSITIVE_STORAGE_PREFIXES.some(
    (prefix) => key === prefix || key.startsWith(`${prefix}:`),
  );
}

function purgePlaintextSensitiveStorage(storage: QueryPersistenceStorage): void {
  const keys = new Set<string>(PLAINTEXT_SENSITIVE_STORAGE_PREFIXES);
  if (typeof storage.length === 'number' && storage.key) {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && isPlaintextSensitiveStorageKey(key)) {
        keys.add(key);
      }
    }
  }
  for (const key of keys) {
    storage.removeItem(key);
  }
}

/**
 * Resolves the storage `persistQueryClient` should write through, or
 * `undefined` to run with no persistence at all.
 *
 * `plaintextCacheDisallowed` covers every shell whose storage is not a
 * browser profile the user can clear: the desktop renderer, and the native
 * WKWebView, where `localStorage` is an unencrypted file in the app container
 * and the query cache is a default-allow denylist held for a 24h `maxAge`.
 * Both shells keep a native session and a live renderer, so the cache buys
 * them nothing a refetch does not.
 *
 * Returning `undefined` only stops FUTURE writes, so this also purges what a
 * previous build already persisted — an upgrading install would otherwise keep
 * serving a plaintext snapshot until `maxAge` evicted it.
 */
export function prepareQueryPersistenceStorage(
  storage: QueryPersistenceStorage,
  plaintextCacheDisallowed: boolean,
): QueryPersistenceStorage | undefined {
  if (!plaintextCacheDisallowed) {
    return storage;
  }
  try {
    purgePlaintextSensitiveStorage(storage);
  } catch {
    // A blocked storage API already means this shell cannot persist.
  }
  return undefined;
}

/**
 * Decides whether a TanStack Query result should be persisted to
 * localStorage by `persistQueryClient`. The persister is configured with
 * `createSyncStoragePersister`, which writes via `JSON.stringify` +
 * `localStorage.setItem` on the main thread — so any query that can hold
 * a large blob, a non-JSON-serialisable structure, or a deploy-sensitive
 * snapshot MUST be excluded here.
 *
 * Extracted from `App.tsx` so the skip-list can be unit-tested.
 */
/**
 * Queries whose data is a `Map`. Maps don't survive `JSON.stringify` (they
 * serialise to `{}`), so a rehydrated snapshot would be a plain object and blow
 * up on `.get(...)`. All are viewer-scoped and short-lived (RLS re-decides
 * visibility / signed URLs re-sign on reload), so they must re-resolve anyway.
 * Grouped into a helper so `shouldDehydrateQuery` stays within its complexity
 * budget. Never persist:
 *   - `correspondence/capabilities` - per-viewer capability Map; also share-gated
 *     (caught earlier by the registry), so this arm is the standalone
 *     serialisation guard: if capabilities ever left the share-gated set it still
 *     must not persist as a corrupted `{}`.
 *   - `correspondence/company-summary` — `Map<companyId, summary>` (CRM org lens).
 *   - `constituents/privacy-badges` — `Map<id, isPrivate>` (CRM list enrichment,
 *     merged in `mergeConstituentEnrichment`).
 *   - `constituents/messenger-avatars` — `Map<path, signedUrl>` (CRM list).
 *   - `constituents-aggregate` / `companies-aggregate` - the CRM facet roll-ups
 *     ({ total, options: Map<fieldId, FacetOption[]> }) from useConstituentFacets /
 *     useCompanyFacets. A rehydrated `{}` would crash the CRM toolbar on `.get(...)`.
 *   - `representationTaskOwnerKeys.all` / `representationTaskStageKeys.all` -
 *     `Map<taskId, ownerIds>` / `Map<taskId, stage>` (min rep rows on Tasks). A
 *     rehydrated `{}` would crash the Tasks list on `.get(...)`.
 */
function isNonSerialisableMapQuery(query: Pick<Query, 'queryKey'>): boolean {
  const key = query.queryKey[0];
  if (key === representationTaskOwnerKeys.all[0] || key === representationTaskStageKeys.all[0]) {
    return true;
  }
  if (key === 'correspondence') {
    return query.queryKey[1] === 'capabilities' || query.queryKey[1] === 'company-summary';
  }
  if (key === 'constituents') {
    // privacy-badges is viewer-scoped, so its key carries userId at index 2 and
    // the discriminator sits at index 3; messenger-avatars is viewer-agnostic
    // (signed URLs) and keeps the discriminator at index 2.
    return query.queryKey[3] === 'privacy-badges' || query.queryKey[2] === 'messenger-avatars';
  }
  return key === 'constituents-aggregate' || key === 'companies-aggregate';
}

/**
 * Queries whose data is a short-lived Supabase Storage signed URL (1-hour TTL)
 * that MUST be re-signed on reload. localStorage persists for 24h (maxAge), so a
 * rehydrated snapshot replays an EXPIRED URL — the storage GET then 400s with
 * `InvalidJWT "exp" claim timestamp check failed` and the asset never renders.
 * Never persist:
 *   - `inbox-messenger-avatars` — a `Map<storagePath, string | null>` (also
 *     non-serialisable: `JSON.stringify(new Map())` is `{}`, so a rehydrated snapshot
 *     blows up on `.get`).
 *   - the whole `brand-logo` root (useBrandLogos) - each `ResolvedBrandLogo` carries a
 *     1-hour signed URL from `createSignedUrls`. The array is JSON-serialisable, so it
 *     is NOT the Map class; it is excluded purely because those URLs perish. A persisted
 *     snapshot replays an EXPIRED URL on the next cold reload (the query defaults disable
 *     refetch-on-mount) and every logo `<img>` 400s.
 *   - the whole `brand-font` root (useBrandFonts) - every `ResolvedBrandFont` in its
 *     `fonts` array carries a 1-hour signed URL to a `brand-fonts` object. Same
 *     perishable class as `brand-logo`, and the consequence is worse: a rehydrated
 *     expired URL 400s the font fetch, so the CE.SDK engine registers a named typeface
 *     whose bytes never arrive and text silently renders in the fallback face. The hook
 *     also runs `gcTime: 0` so an unmounted snapshot cannot resurrect in memory either;
 *     this arm closes the same hole on the durable localStorage leg.
 *   - the whole `brand-templates` root - active-template rows carry 1-hour archive and
 *     preview signed URLs. A persisted library snapshot would replay both after expiry.
 *   - the letterhead-template preview sub-queries under the `org-templates` root
 *     (`preview` / `element-uris` / `snapshot-preview`) — the min-rep preview and the
 *     template editor both fetch these; a persisted expired URL leaves the letterhead
 *     blank. The plain `['org-templates', orgId]` list query holds no URLs and stays
 *     persistable (queryKey[1] is the org id, never one of these sub-labels).
 */
function isEphemeralSignedUrlQuery(query: Pick<Query, 'queryKey'>): boolean {
  const key = query.queryKey[0];
  if (
    key === 'inbox-messenger-avatars' ||
    key === 'brand-logo' ||
    key === 'brand-font' ||
    key === 'brand-templates'
  ) {
    return true;
  }
  if (key === 'org-templates') {
    const sub = query.queryKey[1];
    return sub === 'preview' || sub === 'element-uris' || sub === 'snapshot-preview';
  }
  return false;
}

/**
 * Roots resolved under the viewer's RLS (ADR 0091): cited evidence, signed
 * originals and downloads, knowledge reads. A restored snapshot would hand a
 * later sign-in on this browser another member's originals, and outlive a
 * revoked share or membership. Read from the shared list, so a root added there
 * is never persisted. `storage` and `knowledge` also carry exclusions of their
 * own in `shouldDehydrateQuery`, for size and serialisation.
 */
function isViewerScopedEvidenceQuery(query: Pick<Query, 'queryKey'>): boolean {
  return (VIEWER_SCOPED_EVIDENCE_ROOTS as readonly unknown[]).includes(query.queryKey[0]);
}

/**
 * Anonymous share pages key their queries by the raw bearer TOKEN
 * (['public-ad', …] resolve/status/media, ['public-share', …] resolve/thread).
 * Persisting to localStorage (24h maxAge) would write the token to disk AND replay
 * a revoked/expired share's payload — plus its expiring signed media URLs — on the
 * next cold load. Neither anonymous surface may persist (PUB-894 class).
 */
function isAnonymousShareTokenQuery(query: Pick<Query, 'queryKey'>): boolean {
  const key = query.queryKey[0];
  return key === 'public-ad' || key === 'public-share';
}

/**
 * Two-way door for persisting the correspondence index (`['email-index', …]`).
 * Trade: `true` buys instant cold-start paint of the inbox list; `false` keeps
 * constituent PII (email subjects, snippets, sender names) out of plaintext
 * localStorage, which survives logout and device handoff (PUB-894 class).
 * Flip ritual: change this const AND update the shipped-default pin in
 * query-persistence.test.ts (a deliberate tripwire — the predicate wiring
 * itself tracks the flag and needs no edit). Flipping back OFF additionally
 * requires a persister buster bump — the exclusion only filters future saves,
 * so an already-persisted snapshot keeps hydrating until evicted. Exported for
 * the persistence unit tests.
 */
export const PERSIST_EMAIL_INDEX = false;

/**
 * Viewer-aware reads whose payload is constituent PII and whose visibility RLS
 * must re-decide on reload. Never persist:
 *   - `['company-activity', orgId, companyId]` — the organisation Overview
 *     roll-up. The `get_company_correspondence` RPC is SECURITY INVOKER, and its
 *     `nav` carries email subjects, snippets and sender names. Its sibling
 *     `correspondence/company-summary` is excluded for the same viewer-aware
 *     reason, and that one only holds counts.
 *   - `['correspondence','external-shares',orgId,corrId]` — the external
 *     recipients a thread has been shared with, i.e. their email addresses. The
 *     sibling `shares` key is caught by the share-gated registry; this one sits
 *     outside it.
 *   - `['correspondence','constituent',orgId,constituentId]`
 *     (useConstituentCorrespondence) and `['correspondence','company',orgId,
 *     companyId]` (useCompanyEmails) - the CRM profile / organisation Emails
 *     tabs: one row per thread carrying subject, snippet and from_name. The
 *     company family is fed by the SAME `get_company_correspondence` RPC as
 *     `company-activity` above, and the constituent family's visibility spans
 *     RLS-gated personal-mailbox rows - identical payload, identical
 *     viewer-aware exposure. (The exact-match on the sub-key leaves the
 *     sibling `company-summary` to isNonSerialisableMapQuery.)
 */
function isViewerAwarePiiQuery(query: Pick<Query, 'queryKey'>): boolean {
  const key = query.queryKey[0];
  if (key === 'company-activity') {
    return true;
  }
  if (key !== 'correspondence') {
    return false;
  }
  const sub = query.queryKey[1];
  return sub === 'external-shares' || sub === 'constituent' || sub === 'company';
}

/**
 * Queries whose data is a whole provider message payload — the body itself, not
 * a summary of it. Email bodies routinely embed multi-MB base64 inline images
 * (signatures, screenshots, embedded photos), so the synchronous persister
 * blocks the main thread past Chrome's unresponsive-script threshold when a
 * thread is opened (sibling freeze:
 * refresh-signature-localstorage-freeze-PUB-prod), and the persisted copy is
 * constituent content readable off disk (PUB-894 class). Never persist:
 *   - `[<provider>, 'thread'|'message', …]` — the progressive thread loader
 *     writes this key from the RUNTIME provider value
 *     (`use-email-thread-progressive.ts`), so the match must cover every
 *     provider the app can hold an account for, not just the two that were
 *     enumerated when the exclusion was written. `invalidateEmailCaches` pokes
 *     `gmail` / `outlook` / `imap` for exactly this reason.
 *   - `['correspondence', 'thread'|'message', …]` — `queryKeyForAction` falls
 *     back to the `correspondence` root when a migrated caller omits its
 *     `providerHint`, which would otherwise route a full message payload past
 *     the provider arm above.
 *   - `['correspondence', 'fetch_raw_message', orgId, emailMetadataId]`
 *     (useRawEmailSource) — the ORIGINAL RFC 822 source: every header, every
 *     address, and the undecoded body of one message. `staleTime: Infinity`
 *     means a rehydrated snapshot is never refetched, so persisting it pins the
 *     rawest possible copy of a constituent email in localStorage.
 */
const MESSAGE_PAYLOAD_ROOTS = new Set(['gmail', 'outlook', 'imap', 'correspondence']);

function isFullMessagePayloadQuery(query: Pick<Query, 'queryKey'>): boolean {
  const key = query.queryKey[0];
  const sub = query.queryKey[1];
  if (typeof key !== 'string' || !MESSAGE_PAYLOAD_ROOTS.has(key)) {
    return false;
  }
  return (
    sub === 'thread' ||
    sub === 'message' ||
    sub === 'fetch_raw_message' ||
    // Not a message body, but `senderKey` in the payload can be a sender
    // email address; compared against the shared sub-key (see
    // pii-query-key-roots) so the mint and this exclusion rename together.
    sub === UNSUBSCRIBE_INFO_QUERY_SUBKEY
  );
}

export function shouldDehydrateQuery(query: Pick<Query, 'state' | 'queryKey'>): boolean {
  if (query.state.status !== 'success') {
    return false;
  }
  // Share-gated surfaces: the constituent PII a correspondence_shares /
  // task_shares row confers access to (email subjects/snippets/draft bodies,
  // private-task titles/descriptions, comment bodies, attachment filenames).
  // localStorage outlives a revoke for up to maxAge (24h) - the PUB-894 leak
  // class. Precisely: `AuthProvider.signOut()` DOES remove the persisted cache
  // key, so a user-initiated sign-out is not the exposure. The window is
  // everything else - a closed tab, an expired session, a shared machine, a
  // long-lived renderer - anywhere maxAge runs out before someone clicks Sign
  // out. Every "outlives the session" below means that window, not logout.
  // A persisted snapshot would therefore keep rendering to a user whose share
  // is gone until the in-memory query refetches. Declared once in
  // the share-gated registry so this persistence exclusion and the revoke-time
  // eviction stay in lockstep: a key is excluded here AND evicted on revoke, or
  // neither. See share-gated-cache-registry.
  if (
    isShareGatedQuery(query) ||
    isAnonymousShareTokenQuery(query) ||
    isViewerScopedEvidenceQuery(query)
  ) {
    return false;
  }
  const key = query.queryKey[0];
  if (
    // `['email-thread-db', orgId, threadId]` (emailThreadDbKeys.thread) is the
    // V2 detail panel's whole-thread read: every row carries `body_html` — the
    // rendered body of every message in an opened conversation. Persisting it
    // writes constituent email content to localStorage for the full 24h maxAge,
    // where it outlives the session (PUB-894 class — see the note above for the
    // real window: a user-initiated sign-out DOES clear it), and reruns
    // the same synchronous multi-MB serialise that froze the main thread for the
    // sibling `gmail`/`outlook` thread keys below. Never persist; the query
    // re-reads from the DB (plus Realtime) on mount.
    // A body-bearing exclusion MUST be pinned by a test that builds the key from
    // the hook's exported factory — a hand-typed literal here silently becomes a
    // no-op the day its hook is renamed or deleted, which reads as "bodies are
    // excluded" while the live key persists unguarded.
    key === WEBSITES_QUERY_KEY ||
    // The document editor's emoji set: public data, but ~366K characters per language, which
    // every cache save would re-serialise on the main thread and which crowds the localStorage
    // quota the rest of this snapshot shares. It is a static chunk the browser already caches.
    key === documentEmojiKeys.all[0] ||
    key === 'email-thread-db' ||
    // A message body returned without being stored (the account opted out of
    // content storage): persisting it would store exactly what the opt-out
    // refuses. Built by `fetchedEmailBodyKeys`; pinned by its test.
    key === 'fetched-email-body' ||
    key === 'email-thread-attachments' ||
    key === 'constituent-property-values' ||
    key === 'constituent' ||
    key === 'constituent-email-identifiers' ||
    key === 'constituent-phone-identifiers' ||
    key === 'constituent-email-audit' ||
    // Set-valued dedup caches: `JSON.stringify(new Set())` is `{}`, so a persisted
    // snapshot rehydrates as a plain object - `.has(...)` then throws and every
    // entry is silently lost. A Set-backed query must never be dehydrated.
    key === 'all-constituent-emails' ||
    key === 'all-constituent-phones' ||
    // The social reply-window anchor: a countdown whose hook runs `staleTime: 0`
    // plus a per-minute `refetchInterval` precisely so a stale anchor is never
    // rendered, over a value that is the constituent's last inbound timestamp on
    // a correspondence. Compared against the shared root (see
    // pii-query-key-roots) so the mint and this exclusion rename together.
    key === MESSENGER_WINDOW_QUERY_KEY ||
    key === 'email-stars' ||
    key === 'explorer' ||
    key === 'companies' ||
    // The keyset-paginated Organisations list caches full crm_companies rows
    // per page - the same payload class as the legacy `companies` root excluded
    // above (synchronous persister + large row blobs). Keep the two roots'
    // persistence posture identical.
    key === 'companies-list' ||
    key === 'email-accounts' ||
    key === 'email-accounts-avatars' ||
    key === EXTERNAL_EMAIL_MAILBOXES_QUERY_KEY ||
    // Send read model and sender grants: an authorization answer plus the
    // approval hash; a restored `can_send` would outlive a revoked grant.
    key === EXTERNAL_EMAIL_SEND_QUERY_KEY ||
    key === 'send-as-options' ||
    // The messaging accounts list is viewer-scoped at RLS (personal social
    // accounts are visible only to their owner). Snapshots persisted by
    // builds that keyed on org alone restore for whichever same-org user
    // signs in next, and even the viewer-suffixed key must not persist:
    // account metadata at rest outlives the session, and a restored snapshot
    // resurrects disconnected/purged accounts for up to maxAge with
    // refetch-on-mount off app-wide. Visibility must come from RLS on every
    // load; compared against the shared root (see pii-query-key-roots) so the
    // mint and this exclusion rename together.
    key === MESSAGING_ACCOUNTS_QUERY_KEY ||
    // The two comment/email attachment preview caches. `attachment-preview-blob`
    // resolves to a raw `Blob` (`JSON.stringify` → `{}`, so a rehydrated snapshot
    // silently feeds react-pdf an empty object), and `attachment-office-preview`
    // resolves to the PARSED document — DOCX converted to HTML, spreadsheet rows
    // as values. That is constituent attachment content in plaintext localStorage
    // for 24h, outliving the session (PUB-894 class). Both re-fetch from a fresh signed
    // URL on reload; never persist.
    key === 'attachment-preview-blob' ||
    key === 'attachment-office-preview' ||
    // Unsent drafts. Every one of these caches constituent-facing prose that a
    // staffer has written but not sent, and every one is DB-backed — a reload
    // refetches, so none of them has a reason to be readable off disk:
    //   - `scheduled-drafts` — `email_drafts` rows: subject, body, to/cc/bcc.
    //   - `draft-approvals` / `messaging-draft-approvals` — the approval queues,
    //     org-wide. `draft-approvals` additionally carries `original_body` and
    //     `thread_history` (the quoted inbound email) and the sender signature.
    //   - `representation-drafts` — the full letter body plus a
    //     `recipient_snapshot` holding a constituent's name and postal address.
    //   - `quick-drafts` — `input_text` (the free-text prompt) and
    //     `generated_content` (the drafted reply).
    //   - `messaging-compose-draft` / `messaging-draft-citations` — the
    //     composer's autosaved `messaging_drafts` body and its citation map.
    key === SCHEDULED_DRAFTS_QUERY_KEY ||
    key === 'draft-approvals' ||
    key === 'messaging-draft-approvals' ||
    key === 'representation-drafts' ||
    key === 'quick-drafts' ||
    key === 'messaging-compose-draft' ||
    key === 'messaging-draft-citations' ||
    // Inbound constituent mail held outside the thread caches:
    //   - `rejected-emails` — `forwarded_emails_log` rows INCLUDING
    //     `raw_payload`, the entire forwarded message (headers, body,
    //     attachment names). The highest-exposure key in this group.
    //   - `forwarded-email-list` — the same table's from/name/subject columns.
    //   - `email-metadata` — a whole `email_metadata` row (`select('*')`):
    //     subject, snippet, from/to/cc addresses. The "emails survive reload"
    //     argument behind the `email-index` two-way door (PERSIST_EMAIL_INDEX)
    //     does not extend to a per-email row fetched when a message is opened.
    //   - `activity-email-nav` — subject, snippet, from_name, from_email for the
    //     CRM activity feed's email navigation.
    key === REJECTED_EMAILS_QUERY_KEY ||
    key === FORWARDED_EMAIL_LIST_QUERY_KEY ||
    key === 'email-metadata' ||
    key === 'activity-email-nav' ||
    // `linked-representation-outbound` resolves the sent representation behind a
    // correspondence: subject, full HTML body, recipients, attachment filenames.
    // `review-draft-edits` holds a reviewer's in-progress edits to a draft body.
    // Attachment URL caches hold bearer signed URLs for constituent files.
    key === 'linked-representation-outbound' ||
    // The origin side of the same link: correspondence title + the latest email
    // snippet. Its own root, so excluding `linked-representation-outbound` does
    // not cover it.
    key === 'linked-representation-origin' ||
    key === 'review-draft-edits' ||
    key === 'comment-attachment-url' ||
    key === 'email-inline-attachment-urls' ||
    // Contact-directory reads whose payload — and in two cases whose KEY — is a
    // constituent email address. `recipient-contacts` is the worst of them: the
    // key carries the staff-typed search string, which is routinely a
    // constituent name or address fragment (the `searchTerm` class the logging
    // rules already forbid), so persisting it writes the search itself to disk.
    key === RECIPIENT_CONTACTS_QUERY_KEY ||
    // `['representation-recipients', orgId, {search, …}]` is the same shape as
    // `recipient-contacts`: name + email + postal_address in the payload, and
    // the staff-typed search string in the key. A ministerial directory rather
    // than constituents, but identical exposure.
    key === 'representation-recipients' ||
    // `['standalone-correspondence-metadata', corrId]` selects the
    // correspondence `title`, which for an ingested email IS its subject
    // (`ingest-forwarded-email` writes `title: 'Email: <subject>'`). Reached on
    // the deep-link / tab-restore path for any correspondence whose selection
    // lacks a channel, email included.
    key === 'standalone-correspondence-metadata' ||
    key === 'inbox-sender-avatars' ||
    key === 'email-contact-avatars' ||
    key === 'account-avatar-by-email' ||
    // The `knowledge` root: `['knowledge','documents','detail',orgId,actorId,docId]`
    // resolves `extracted_text` — the entire extracted body of every knowledge
    // document opened, and the largest single payload left facing the
    // synchronous persister. The sibling list query deliberately omits that
    // column as "the heavy text/jsonb columns"; persisting the detail row
    // reintroduces exactly the main-thread freeze this file exists to prevent.
    // Excluding the root keeps every future knowledgeKeys.* sub-key covered.
    key === 'knowledge' ||
    // The `documents` root (authored documents, NOT knowledge uploads):
    // `['documents',orgId,userId,'detail',docId]` resolves the full row
    // including `body` — constituent-authored prose — and the list/link/search
    // sub-keys resolve titles. Visibility is VIEWER-scoped: the `documents`
    // SELECT policy admits a row on `NOT is_private OR created_by = auth.uid()`.
    // The userId in the key closes the in-memory gcTime leg; excluding the root
    // here closes the 24h localStorage leg, which outlives the session on a
    // shared machine (the archived-constituents class above). Authored document
    // bodies must not outlive the session in localStorage.
    key === 'documents' ||
    // `['document-count',orgId,userId]` is a viewer-scoped count kept off the
    // `documents` root so autosaves do not refetch it; it holds no title, but it
    // does reflect the viewer's own private rows, so it stays out alongside them.
    key === 'document-count' ||
    // `['document-search',orgId,userId,query]` and `['task-search',...]` are
    // picker result sets of titles under the same viewer-scoped policies, and
    // sit outside the `documents` root, so they need naming separately.
    key === 'document-search' ||
    key === 'task-search' ||
    // The picker search roots behind the linked sidebars. Every relation they
    // read is viewer-gated, not org-gated — `user_can_see_constituent`,
    // `user_can_see_company`, and a correspondence RPC that returns
    // personal-account rows only for `auth.uid()` — so the cached result sets
    // carry one member's private names, domains, subjects and snippets. The
    // viewer in the key closes the in-memory leg; these close the 24h
    // localStorage leg on a shared machine.
    key === 'task-linked' ||
    key === 'task-linked-correspondence' ||
    // `['attachments-by-ids', ids]` (useAttachmentsByIds) resolves task_attachments
    // rows with an embedded 1-hour signed URL (`createSignedUrls`, 3600s) plus the
    // filename. A persisted row replays an EXPIRED download URL on the next cold
    // reload (this hook has no refetchInterval/focus mitigation). The sibling
    // `['task-attachments', …]` key is excluded via the share-gated registry; this
    // by-ids key sits outside it. Also carries filenames (PII, PUB-894). Never persist.
    key === 'attachments-by-ids' ||
    // The `storage` root (useStorageSignedUrl / useStorageDownload):
    // `['storage','signed-url',…]` is a raw 1-hour signed URL, and
    // `['storage','download',…]` is a `{ blob, text }` — the Blob is non-serialisable
    // (`JSON.stringify` → `{}`) and the text is file content. Both must re-fetch on
    // reload; a persisted signed URL replays expired (react-pdf then 400s). Never persist.
    key === 'storage' ||
    key === 'account-viewing-context' ||
    key === 'organization-members-cache' ||
    key === 'user-organizations' ||
    // `['organizations','all']` (useOrganizations) is the platform-admin roster
    // of EVERY tenant - name, description, logo and settings for organisations
    // the viewer is not a member of, which no other persisted key spans. On a
    // shared machine it outlives the session for the full 24h maxAge, and a
    // viewer who loses the admin role keeps rendering it (PUB-894 class). It is
    // also the resurrection class: the hook's only refetch trigger is an
    // explicit invalidate from this tab, so a persisted snapshot freezes the
    // roster across reloads until maxAge elapses.
    key === 'organizations' ||
    key === 'is-platform-admin' ||
    // Notification rows can carry constituent names, subjects, task titles,
    // comment context, and metadata. The native shell keeps its renderer alive,
    // but a cold start must reconcile these rows from RLS instead of restoring
    // sensitive content from the 24-hour localStorage cache.
    key === 'notifications' ||
    // Broadcast lifecycle (list + detail + nested content-versions / events, all
    // under the `broadcasts` root). Lifecycle status is cron-mutated server-side
    // and realtime (`use-broadcast-realtime`) is the authoritative live path; the
    // app defaults disable refetch-on-mount/reconnect and persist for 24h, so a
    // persisted snapshot resurrects a stale "Scheduled"/"Sending" status across a
    // reload until staleTime elapses - the same resurrection class already
    // excluded for pulse-ai approval cards below. The transient `liveSend`
    // progress field also lives on these rows and must never reach localStorage.
    // The root ALSO carries PII sub-families that must never be dehydrated: the
    // who-opened engagement list (`…detail/{id}/engagement/…` — recipient
    // emails + constituent names, the broadcast-suppressions class) and the
    // per-link click stats (`…detail/{id}/link-stats` — untrusted clicked urls
    // that can embed per-recipient tokens). Narrowing this root exclusion MUST
    // keep both families excluded (their tests pin this). Always refetch on
    // reload. (PII keys `broadcast-suppressions` / `broadcast-sending-domains`
    // are separate roots, excluded below/above; the org-config
    // `broadcast-brand-kit` root stays persistable.)
    key === 'broadcasts' ||
    // Broadcast suppression list rows carry `email_normalized` — constituent
    // email addresses. localStorage outlives the session / device handoff (PUB-894
    // class), so this PII MUST NOT be dehydrated. Always refetch on reload.
    key === 'broadcast-suppressions' ||
    // The sending-domains envelope carries sender-identity from/reply-to
    // addresses (the office's configured sending addresses) — same persistence
    // sensitivity as `email-accounts` / `send-as-options`, which are excluded
    // above. Keep it out of localStorage for consistency.
    key === 'broadcast-sending-domains' ||
    // Cross-org admin payload — by_user includes staff names + emails and
    // by_org spans every tenant. MUST NOT land in localStorage, where it would
    // outlive the session and be readable by anyone with device access (PUB-894).
    key === 'platform-usage' ||
    // The whole billing-admin root (billingAdminKeys.root(), use-billing-admin.ts).
    // `agreements` rows carry `billing_contact_email` and free-text `notes` for
    // EVERY tenant — `billing_agreements` is on the personal-data list in
    // AGENTS.md — and the same root holds the config row and the Xero health
    // probe. localStorage outlives the session and device handoff (PUB-894
    // class, the platform-usage arm above), and with refetchOnMount off by
    // default a persisted snapshot would also serve 24h-old financial terms on a
    // cold load. Excluding the root keeps every future billingAdminKeys.*
    // sub-key excluded by construction; always refetch on reload.
    key === 'billing-admin' ||
    // The whole reporting root: widget aggregates, dashboard list/detail, and
    // filter-value lists. The drill-down hydration cache holds task titles and
    // the aggregates hold per-person counts, and `filter-values` on
    // `assigned_to` carries staff names — localStorage
    // outlives the session and device handoff (PUB-894 class). Dashboard definitions
    // ride along because the root is excluded wholesale; they are a cheap
    // RLS-scoped read and every widget number recomputes server-side, so no
    // reporting surface is worth persisting.
    key === 'reporting' ||
    // The creative namespace (design library, design detail rows, brand
    // settings, and the resolved creative brand under ['creative', orgId,
    // 'brand']) is org-mutated config whose write paths (Brand Studio kit +
    // logo saves, editor scene save) repoint the underlying rows immediately
    // before a reload is plausible. A persisted snapshot resurrects the
    // pre-save row across a reload (refetchOnMount:false - the
    // notification-preferences resurrection class below), and a stale design
    // row would boot the CE.SDK editor from a superseded scene_path. Cheap
    // RLS-scoped reads; always refetch on reload. Every creativeKeys.* key
    // nests under this root, so new creative reads are excluded by
    // construction - keep them under the factory.
    key === 'creative' ||
    // Lifecycle and account labels require fresh RLS on reload. A throttled
    // disk snapshot can otherwise resurrect the pre-connect state.
    key === integrationsRootKey()[0] ||
    // Manual brand-draft review payloads carry extracted brand voice plus
    // one-hour signed URLs for derived logo candidates. Neither belongs in the
    // 24-hour localStorage cache: voice is prompt input and an expired preview
    // URL would replay a broken image. The root exclusion covers history,
    // detail, and every future review sub-query.
    key === 'brand-drafts' ||
    // The canonical Brand Studio kit root (brandKitKeys.root(), useBrandKit). Its
    // dual-write RPC and conflict handler invalidate this root on every save, but
    // refetchOnMount:false + staleTime 30s let a persisted snapshot resurrect the
    // pre-save kit across a reload (the creative resurrection class above).
    key === 'brand-kit' ||
    // `['archived-constituents', orgId, userId]` caches full ConstituentWithTags
    // rows (names, emails) whose visibility is viewer-scoped. The userId suffix
    // closes the in-memory gcTime cross-user leak; excluding it here closes the
    // longer-lived 24h localStorage leg (outlives the session, PUB-894 class). Archived
    // PII must not outlive the session in localStorage.
    key === 'archived-constituents' ||
    // The whole Clip Studio root (clipStudioKeys.root()). Project titles and
    // run summaries carry org-sensitive labels today, and the U13/U14 subtrees
    // under the same root add transcript snippets, guidance-derived rationales,
    // and clip specs — transcript-adjacent data that MUST NOT reach
    // localStorage (plan R15; the broadcasts exclusion class above). Run and
    // upload statuses are also live pipeline state a 24h snapshot would
    // resurrect stale. Excluding the root keeps every future sub-key excluded
    // by construction; always refetch on reload.
    key === 'clip-studio' ||
    // The whole Ads Explorer root (ads-keys.ts). `['ads','detail',…]` carries
    // the org-scoped, reveal-gated analysis payload (`ads_detail_analysis`
    // membership-verifies per request and org topic overrides relabel per
    // org), `['ads','media',…]` carries signed GCS URLs, and list/facet pages
    // are `ads_explorer`-gated large-row blobs (synchronous-persister class).
    // With refetchOnMount:false a persisted snapshot would serve another
    // org's — or a gate-revoked user's — data for 24h without re-consulting
    // RLS or the gate (PUB-894 class; localStorage outlives the session).
    // `['ads','shares',…]` additionally carries each live link's RAW bearer
    // token (stored for re-copy since 20260803001227) — a share token written
    // to plaintext disk. Excluding the root keeps every future ads sub-key
    // excluded by construction; the shares pin in the test file guards it.
    key === 'ads' ||
    // `['crm-freemail-domains']` (useFreemailDomains) is the global list of
    // public-provider domains a sender is classified against. The hook refreshes
    // it on a one-hour interval, so a hydrated snapshot is what classifies every
    // sender from first paint until that interval first elapses: persisting it
    // hands that window a list up to maxAge (24h) old, and a provider missing
    // from a stale copy stamps a company logo on a personal address. One cheap
    // authenticated read on mount buys a set that is current from the start. The
    // literal is pinned in the test file against the hook's exported root so a
    // rename cannot leave a dead exclusion behind.
    key === 'crm-freemail-domains'
  ) {
    return false;
  }
  if (key === EMAIL_INDEX_KEY) {
    return PERSIST_EMAIL_INDEX;
  }
  // Map-valued queries (capabilities, company-summary, CRM list privacy-badges /
  // messenger-avatars, min rep task owners / stages) — see isNonSerialisableMapQuery.
  if (isNonSerialisableMapQuery(query)) {
    return false;
  }
  // The keyset-paginated People list caches full `constituents` rows per
  // infinite-query page under `['constituents', orgId, userId, <rpc params>,
  // pageSize]` - the same large-row-blob payload class as the
  // `companies` / `companies-list` roots excluded above (synchronous persister).
  // Identified by the serialized RPC params OBJECT at index 3, whatever fields it
  // currently holds; the sibling `constituents` enrichment caches
  // (`privacy-badges` / `messenger-avatars`, excluded as Maps in
  // isNonSerialisableMapQuery) carry a discriminator string or an id/path array
  // there instead, so this arm never catches them. Matching on the TYPE at index 3
  // rather than a field list keeps the exclusion intact as scope dimensions are
  // added. Keep the list root out of localStorage; always refetch on reload.
  if (
    key === 'constituents' &&
    typeof query.queryKey[3] === 'object' &&
    query.queryKey[3] !== null &&
    !Array.isArray(query.queryKey[3])
  ) {
    return false;
  }
  if (isViewerAwarePiiQuery(query)) {
    return false;
  }
  // Short-lived signed-URL caches (inbox-messenger-avatars + the org-templates
  // letterhead preview sub-queries) — see isEphemeralSignedUrlQuery.
  if (isEphemeralSignedUrlQuery(query)) {
    return false;
  }
  // Pulse AI chat queries hold constituent content end to end. The capability,
  // tool-permission, user-pref, admin-review, and policy-audit roots are all
  // authorization-derived state (provider policy, model selectors, per-tool
  // permissions, oversight flags, admin-only audit rows); restoring any of them
  // from the 24-hour cache can expose controls or policy the organization has
  // since revoked - or hide newly granted ones - because the app disables
  // refetch-on-mount by default. Always revalidate every pulse-ai root.
  if (
    key === 'pulse-ai' ||
    key === 'pulse-ai-capabilities' ||
    key === 'pulse-ai-tool-permissions' ||
    key === 'pulse-ai-user-tool-prefs' ||
    key === 'pulse-ai-admin-review-enabled' ||
    key === 'pulse-ai-tool-policy-audit'
  ) {
    return false;
  }
  // Two roots in the same resurrection class as broadcasts / pulse-ai above: the
  // app defaults set refetchOnMount:false, so a persisted snapshot (24h maxAge)
  // wins on the next cold load and revives state the server has since changed.
  //   - `['notification-preferences', orgId, userId]` — staleTime 10m, so the
  //     snapshot resurrects the pre-save preferences across a reload within
  //     staleTime. The write path seeds the cache on success, but the durable
  //     localStorage copy would still win cold.
  //   - `passkeys` (PASSKEY_QUERY_KEYS.root(), use-passkeys.ts) — the viewer's
  //     enrolled WebAuthn credentials: ids, friendly names ("Will's iPhone" —
  //     PII-adjacent by design) and last-used timestamps. localStorage outlives
  //     the session and device handoff (PUB-894 class), and the list is
  //     authentication state whose server truth also drives the login hint, so a
  //     rehydrated snapshot would resurrect a credential an admin has since
  //     revoked. Configured `staleTime: 0` + `refetchOnMount` precisely so it
  //     always re-reads; persisting it would defeat that. Compared against the
  //     shared root, not a re-typed literal, so renaming it moves the factory and
  //     this exclusion together instead of silently leaving a dead exclusion
  //     behind (see docs/solutions/security-issues/
  //     email-thread-body-persisted-to-localstorage-dead-exclusion-key.md).
  //   - `crm-import-jobs` (crmImportJobKeys.all, use-crm-import-job.ts) — the
  //     import wizard's whole root, excluded here rather than per sub-key so
  //     every future `crmImportJobKeys.*` inherits it by construction. Its
  //     `preview` sub-key holds the raw uploaded cells: constituent names,
  //     emails, phones and addresses lifted straight off the operator's file,
  //     which is why the RPC producing them is admin-gated. It is the worst
  //     member of this resurrection class, not merely another one — the preview
  //     query overrides neither refetchOnWindowFocus nor refetchOnReconnect and
  //     inherits the app default refetchOnMount:false, so a rehydrated snapshot
  //     has NO refetch trigger at all; and because the wizard restores its jobId
  //     from localStorage too, the resumed key matches by construction, so the
  //     stale snapshot IS served and seeds the mapping UI without the admin-role
  //     gate ever being re-consulted.
  //   - `on-behalf-send-capability` (ON_BEHALF_SEND_CAPABILITY_QUERY_KEY,
  //     use-on-behalf-send-capability.ts) — the answer to "may this user send a
  //     reply from their own account on a shared inbox". An authorization
  //     `true` in localStorage for the full 24h maxAge would keep the composer
  //     affordance live after the grant was withdrawn; the hook is `staleTime:
  //     0` + refetch-on-mount precisely so the answer is re-derived every load.
  //     Compared against the shared root rather than a re-typed literal, same
  //     reason as `passkeys` above.
  if (
    key === 'notification-preferences' ||
    key === PASSKEYS_QUERY_KEY ||
    key === POTENTIAL_DUPLICATES_QUERY_KEY ||
    key === CRM_IMPORT_JOBS_QUERY_KEY ||
    key === ON_BEHALF_SEND_CAPABILITY_QUERY_KEY
  ) {
    return false;
  }
  if (isFullMessagePayloadQuery(query)) {
    return false;
  }
  return true;
}
